// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module useAnalyticsDashboard
 * @package @geenius/analytics/react
 * @description Normalizes dashboard page inputs into a stable React state
 * object for hosts that compose the React analytics dashboard manually.
 */

import type {
  AnalyticsDashboardData,
  AnalyticsEvent,
  CohortAnalysis,
  FunnelResult,
  RealtimeStats,
} from "@geenius/analytics";
import { useMemo } from "react";
import type { AnalyticsDashboardPageProps } from "../pages/AnalyticsDashboardPage";

/** Input data consumed by {@link useAnalyticsDashboard}. */
export interface UseAnalyticsDashboardOptions {
  data?: AnalyticsDashboardData | null;
  recentEvents?: AnalyticsEvent[];
  realtimeStats?: RealtimeStats | null;
  funnelResult?: FunnelResult | null;
  cohortAnalysis?: CohortAnalysis | null;
  conversionRate?: number | null;
  isLoading?: boolean;
  isRealtimeLoading?: boolean;
  error?: AnalyticsDashboardPageProps["error"];
  onRetry?: AnalyticsDashboardPageProps["onRetry"];
}

/** Numeric dashboard summary derived by {@link useAnalyticsDashboard}. */
export interface AnalyticsDashboardSummary {
  totalPageViews: number;
  uniqueVisitors: number;
  topPageCount: number;
  recentEventCount: number;
  activeVisitors: number;
}

/** Reactive dashboard state returned by {@link useAnalyticsDashboard}. */
export interface UseAnalyticsDashboardReturn {
  data: AnalyticsDashboardData | null;
  recentEvents: AnalyticsEvent[];
  realtimeStats: RealtimeStats | null;
  funnelResult: FunnelResult | null;
  cohortAnalysis: CohortAnalysis | null;
  conversionRate: number | null;
  isLoading: boolean;
  isRealtimeLoading: boolean;
  error: AnalyticsDashboardPageProps["error"];
  hasDashboardData: boolean;
  hasRealtimeData: boolean;
  isEmpty: boolean;
  summary: AnalyticsDashboardSummary;
  pageProps: Pick<
    AnalyticsDashboardPageProps,
    | "cohortAnalysis"
    | "conversionRate"
    | "data"
    | "error"
    | "funnelResult"
    | "isLoading"
    | "isRealtimeLoading"
    | "onRetry"
    | "recentEvents"
    | "realtimeStats"
  >;
}

/**
 * Builds a stable dashboard view model for the React analytics dashboard component.
 *
 * @param options Dashboard data, realtime stats, events, and state flags.
 * @returns Normalized dashboard state and page-ready props.
 */
export function useAnalyticsDashboard(
  options: UseAnalyticsDashboardOptions = {},
): UseAnalyticsDashboardReturn {
  const data = options.data ?? null;
  const recentEvents = options.recentEvents ?? [];
  const realtimeStats = options.realtimeStats ?? null;
  const funnelResult = options.funnelResult ?? null;
  const cohortAnalysis = options.cohortAnalysis ?? null;
  const conversionRate = options.conversionRate ?? null;
  const isLoading = options.isLoading ?? false;
  const isRealtimeLoading = options.isRealtimeLoading ?? false;
  const error = options.error ?? null;

  const summary = useMemo<AnalyticsDashboardSummary>(
    () => ({
      activeVisitors: realtimeStats?.activeVisitors ?? 0,
      recentEventCount: recentEvents.length,
      topPageCount: data?.topPages.length ?? 0,
      totalPageViews: data?.totalPageViews ?? 0,
      uniqueVisitors: data?.uniqueVisitors ?? 0,
    }),
    [data, recentEvents.length, realtimeStats],
  );

  const pageProps = useMemo<UseAnalyticsDashboardReturn["pageProps"]>(
    () => ({
      cohortAnalysis,
      conversionRate,
      data,
      error,
      funnelResult,
      isLoading,
      isRealtimeLoading,
      onRetry: options.onRetry,
      recentEvents,
      realtimeStats,
    }),
    [
      cohortAnalysis,
      conversionRate,
      data,
      error,
      funnelResult,
      isLoading,
      isRealtimeLoading,
      options.onRetry,
      recentEvents,
      realtimeStats,
    ],
  );

  return useMemo(
    () => ({
      cohortAnalysis,
      conversionRate,
      data,
      error,
      funnelResult,
      hasDashboardData:
        (data !== null &&
          (data.totalPageViews > 0 ||
            data.uniqueVisitors > 0 ||
            data.topPages.length > 0)) ||
        conversionRate !== null,
      hasRealtimeData: realtimeStats !== null,
      isEmpty:
        !isLoading &&
        !error &&
        data === null &&
        realtimeStats === null &&
        recentEvents.length === 0 &&
        funnelResult === null &&
        cohortAnalysis === null &&
        conversionRate === null,
      isLoading,
      isRealtimeLoading,
      pageProps,
      realtimeStats,
      recentEvents,
      summary,
    }),
    [
      cohortAnalysis,
      conversionRate,
      data,
      error,
      funnelResult,
      isLoading,
      isRealtimeLoading,
      pageProps,
      realtimeStats,
      recentEvents,
      summary,
    ],
  );
}
